if (!customElements.get("mini-cart-cross-sells")) {
  // `data-source` tiers, in the merchant's priority order, mapped to the
  // endpoint `intent` each one needs. "metafield" has no intent: the section
  // reads `product.metafields.custom.cross_sells` from whatever response we already
  // asked for, so it rides along for free.
  const TIER_INTENTS = {
    metafield: null,
    complementary: "complementary",
    recommended: "related",
  };
  // Object.keys, not `tier in TIER_INTENTS` — `in` walks the prototype chain, so
  // a data-source of "constructor" or "toString" would pass the check and then
  // resolve to a function that lands in the request URL.
  const KNOWN_TIERS = Object.keys(TIER_INTENTS);
  const DEFAULT_TIERS = ["recommended"];

  // sessionStorage keys, following the theme's bare camelCase convention
  // (compareMasterToggleState, recentlyViewedProducts).
  const EXPANDED_KEY = "miniCartCrossSellsExpanded";
  const CARDS_KEY_PREFIX = "miniCartCrossSellsCards";

  // sessionStorage can be missing or throwing (private windows, quota,
  // blocked cookies). Every failure here degrades to plain fetch-on-open —
  // the feature works, it just stops remembering across pageviews.
  const readStorage = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Degrading silently is the point; nothing to do.
    }
  };

  // Resolved recommendation cards per source product id, in two layers:
  //
  //  - This Map, shared by every <mini-cart-cross-sells> instance. The mini
  //    cart is re-rendered from the server on each cart change, which destroys
  //    and recreates the element, so it has to live outside the class to
  //    survive within a pageview.
  //  - sessionStorage (hydrate/persist below), which carries the same entries
  //    across pageviews so the spinner is paid once per visit, not once per
  //    page. The session is the cache's whole lifetime — recommendations are
  //    stable enough over one visit that there is no expiry logic, and a card
  //    showing a price a few minutes stale is corrected by its product page.
  //
  // Failed requests never enter either layer (see resolveRecommendations), so
  // a transient blip is retried on the next opportunity instead of being
  // pinned empty for the rest of the session.
  const cache = new Map();

  // In-flight request per source product id. load()'s parallel warm-up and
  // select()'s walk both land here and share one promise, so a source is never
  // fetched twice concurrently; settled entries are removed so a failure can
  // be retried by a later pass.
  const inflight = new Map();

  // Which storage key the Map has been hydrated from, so it happens once per
  // pageview. The key embeds the settings that shape a response (tiers,
  // limit); a merchant changing them mid-session just orphans the old entry,
  // which dies with the tab.
  let hydratedKey = null;

  const cardsKeyFor = (tiers, limit) => `${CARDS_KEY_PREFIX}:${tiers.join(",")}:${limit}`;

  const hydrate = (key) => {
    // Don't cache in the editor. Merchants should be able to see fresh results.
    if (Shopify.designMode || hydratedKey === key) {
      return;
    }

    hydratedKey = key;

    const stored = readStorage(key);

    if (!stored) {
      return;
    }

    try {
      const entries = JSON.parse(stored);

      Object.entries(entries).forEach(([productId, htmls]) => {
        // In-memory entries are fresher than stored ones — never clobber.
        if (cache.has(productId) || !Array.isArray(htmls)) {
          return;
        }

        // Same parse-and-select as fetchIntent, so anything that is not a
        // card (or not even markup) filters out instead of rendering.
        const html = new DOMParser().parseFromString(htmls.join(""), "text/html");

        cache.set(productId, Array.from(html.querySelectorAll(".mini-cart-cross-sell-card")));
      });
    } catch {
      // Corrupt JSON — ignore it; the next persist() overwrites it.
    }
  };

  const persist = (key) => {
    // Don't cache in the editor. Merchants should be able to see fresh results.
    if (Shopify.designMode) {
      return;
    }

    const entries = {};

    cache.forEach((cards, productId) => {
      entries[productId] = cards.map((card) => card.outerHTML);
    });

    writeStorage(key, JSON.stringify(entries));
  };

  // Whether the <details> is expanded. The footer is re-rendered by
  // `scripts.js`, `cart-coupon.js` and `bulk-order-list.js`, each of which
  // replaces its innerHTML and so destroys this element. Keeping the flag in
  // module scope means the state is restored on the next connectedCallback no
  // matter which of them triggered the swap — including any added later —
  // without every one of them having to know we exist.
  //
  // `cart-coupon.js` solves the same problem for itself by cloning
  // `.minicart-coupon` and grafting it back after the swap (see its
  // renderContents), but that only survives coupon-triggered re-renders: on a
  // quantity change its <details> silently reverts to closed. Self-restoring
  // covers every path instead of one.
  //
  // Backed by sessionStorage so the choice also survives navigation: a shopper
  // who collapsed the suggestions keeps them collapsed for the whole visit —
  // and while collapsed we never fetch at all (see maybeLoad), so opting out
  // also opts out of the requests.
  let expanded = readStorage(EXPANDED_KEY) !== "false";

  class MiniCartCrossSells extends HTMLElement {
    constructor() {
      super();
      this.controller = new AbortController();
    }

    connectedCallback() {
      this.list = this.querySelector(".mini-cart-cross-sells-list");
      this.details = this.querySelector(".mini-cart-cross-sells-dropdown");
      this.count = parseInt(this.dataset.count, 10);
      // The recommendations API has a hard limit of 10.
      this.limit = Math.min(this.count, 10);
      this.tiers = (this.dataset.source || "")
        .split(",")
        .map((tier) => tier.trim())
        .filter((tier) => KNOWN_TIERS.includes(tier));

      if (!this.tiers.length) {
        this.tiers = DEFAULT_TIERS;
      }

      this.sources = (this.dataset.productIds || "").split(",").filter(Boolean);

      if (!this.list || !this.details || !this.count || !this.sources.length) {
        // Nothing will ever load into this — do not leave a spinner up
        // promising otherwise.
        this.setAttribute("hidden", "");
        return;
      }

      this.storageKey = cardsKeyFor(this.tiers, this.limit);
      this.loadStarted = false;

      // The drawer we live in decides when to fetch. Its open() sets isOpen
      // before dispatching `${eventPrefix}:open` on document, so the listener
      // below passes maybeLoad's isOpen guard the moment the event arrives.
      // Without a drawer or a prefix to listen for (markup moved, attribute
      // dropped), there is no open moment to wait for — waitsForDrawer then
      // falls back to loading at connect rather than never.
      this.drawer = this.closest("drawer-modal");

      const eventPrefix = this.drawer?.dataset.eventPrefix;

      this.waitsForDrawer = Boolean(this.drawer && eventPrefix);

      if (this.waitsForDrawer) {
        document.addEventListener(`${eventPrefix}:open`, () => this.maybeLoad(), {
          signal: this.controller.signal,
        });
      }

      // Restore the expanded state the shopper last chose. Synchronous within
      // the element upgrade, so a collapsed dropdown never flashes open first.
      this.details.open = expanded;
      this.details.addEventListener(
        "toggle",
        () => {
          expanded = this.details.open;
          writeStorage(EXPANDED_KEY, String(expanded));

          if (this.details.open) {
            // Expanding a collapsed dropdown is the shopper opting back in —
            // the fetch they skipped by collapsing starts now.
            this.maybeLoad();

            // Cards that landed while the dropdown was collapsed measured at
            // zero width (a closed <details> hides its content), so remeasure
            // whenever it opens.
            this.refreshCarousel();
          }
        },
        { signal: this.controller.signal },
      );

      // Pages restored from the back/forward cache come back frozen exactly as
      // they were left: connectedCallback does not re-run, so a preference the
      // shopper changed on a later page never got applied here, and a spinner
      // that was showing at freeze time is still showing. Re-sync from storage
      // and give maybeLoad a chance to resolve that stale spinner (it no-ops
      // unless the drawer is open and the dropdown expanded).
      window.addEventListener(
        "pageshow",
        (event) => {
          if (!event.persisted) {
            return;
          }

          expanded = readStorage(EXPANDED_KEY) !== "false";
          this.details.open = expanded;
          this.maybeLoad();
        },
        { signal: this.controller.signal },
      );

      // Covers reconnecting while the drawer is already up — quantity changes
      // and coupons re-render the footer mid-view — plus the no-drawer
      // fallback. With the drawer closed this is a no-op: recommendations cost
      // one or two requests per cart item and most pageviews never open the
      // mini cart, so loading eagerly would spend that on every page for
      // nothing. Waiting loses nothing in the add-to-cart flow either — it
      // re-renders the footer and would refetch at open anyway — and the
      // session cache makes every open after the first one instant.
      this.maybeLoad();
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    // The single gate for starting a load: at most once per connect, only
    // with the drawer open (when there is one to watch), only with the
    // dropdown expanded. Callers just report their moment — drawer opened,
    // details expanded, element connected — and this decides whether it is
    // time.
    maybeLoad() {
      if (this.loadStarted) {
        return;
      }

      if (this.waitsForDrawer && !this.drawer.isOpen) {
        return;
      }

      if (!this.details.open) {
        return;
      }

      this.loadStarted = true;
      this.load();
    }

    async load() {
      hydrate(this.storageKey);

      // Warm every source select() is certain to consult — the first
      // min(count, sources) round-robin slots visit exactly these — so their
      // requests run in parallel and the spinner costs one round-trip, not a
      // serial walk of the cart. select() picks the same promises up through
      // `inflight`. Sources past the slice are only consulted on a shortfall,
      // and still fetch serially as the walk reaches them.
      this.sources.slice(0, this.count).forEach((source) => {
        this.recommendationsFor(source);
      });

      const picks = await this.select();

      if (!this.isConnected) {
        return;
      }

      // Nothing to offer — no products, or every request failed. The open
      // dropdown has been promising content with its spinner, so take the
      // whole section down rather than leave either standing.
      if (!picks.length) {
        this.setAttribute("hidden", "");
        return;
      }

      this.querySelector(".mini-cart-cross-sells-spinner")?.remove();

      // Each card goes into a .grid-item wrapper: that is the cell selector
      // carousel-slider.js hands to Flickity. The cache keeps bare cards.
      // Appending straight to the list is correct in every state, because
      // refreshCarousel() rebuilds the carousel from the list's children.
      picks.forEach((card) => {
        const item = document.createElement("div");
        item.className = "grid-item";
        item.appendChild(card.cloneNode(true));
        this.list.appendChild(item);
      });
      this.list.dataset.cards = picks.length;

      // Three or fewer cards all fit the visible run (see the data-cards CSS),
      // so the arrows could never advance anything — drop them rather than
      // show a permanently disabled control.
      if (picks.length <= 3) {
        this.querySelector("carousel-navigation")?.setAttribute("hidden", "");
      }

      this.refreshCarousel();
    }

    // Full teardown-and-rebuild, never an incremental patch. carousel-slider.js
    // builds its Flickity the moment the footer renders — on a list that is
    // still empty, and display:none whenever the dropdown sits collapsed —
    // and Flickity freezes cell positions as
    // inline styles at measurement time, so anything measured while hidden
    // stays piled at x=0 even after the cards themselves render at full size.
    // deactivate() moves every card back into the list and drops the stale
    // state; watchCSS() then re-activates from scratch — collect, measure,
    // position — but only at viewport widths where the carousel CSS applies.
    // Below the tablet breakpoint the list simply stays the native swipe
    // scroller. Idempotent, and cheap at this scale (ten cells at most).
    refreshCarousel() {
      const flickity = window.Flickity && Flickity.data(this.list);

      if (!flickity) {
        return;
      }

      flickity.deactivate();
      flickity.watchCSS();
    }

    // Round-robin: slot i draws from sources[i % sources.length], taking that
    // source's next recommendation we have not used yet. A source with none
    // left is exhausted and skipped from then on. Terminates because every
    // pass either fills a slot or exhausts a source.
    async select() {
      const used = new Set(this.sources); // never recommend what is already in the cart
      const cursors = new Map();
      const exhausted = new Set();
      const picks = [];
      let slot = 0;

      while (picks.length < this.count && exhausted.size < this.sources.length) {
        const source = this.sources[slot % this.sources.length];
        slot++;

        if (exhausted.has(source)) {
          continue;
        }

        const cards = await this.recommendationsFor(source);

        if (!this.isConnected) {
          return [];
        }

        let cursor = cursors.get(source) || 0;
        let pick = null;

        while (cursor < cards.length) {
          const card = cards[cursor];
          cursor++;

          if (!used.has(card.dataset.productId)) {
            pick = card;
            break;
          }
        }

        cursors.set(source, cursor);

        if (pick) {
          used.add(pick.dataset.productId);
          picks.push(pick);
        } else {
          exhausted.add(source);
        }
      }

      return picks;
    }

    // Memoized front for resolveRecommendations: a settled answer comes from
    // the cache (either layer, via hydrate), an unsettled one joins the
    // request already in flight, and only a source nobody has asked about yet
    // starts a new one.
    async recommendationsFor(productId) {
      if (cache.has(productId)) {
        return cache.get(productId);
      }

      if (inflight.has(productId)) {
        return inflight.get(productId);
      }

      const request = this.resolveRecommendations(productId).finally(() => {
        inflight.delete(productId);
      });

      inflight.set(productId, request);

      return request;
    }

    // Walks `data-source` in the merchant's priority order and returns the
    // first tier that offers anything. A tier that returns products wins
    // outright — we do not top it up from a lower tier, so a merchant who
    // curated exactly two cross-sells gets those two rather than two plus an
    // algorithmic filler.
    //
    // Costs at most two requests per source, not one per tier: the metafield
    // rides along with whichever intent we ask for first, so it is only ever
    // the two intents that need separate trips.
    async resolveRecommendations(productId) {
      // Prefer an intent we are going to ask for anyway, so the metafield tier
      // adds no request. With no recommendation tier listed at all, we still
      // need one response to read the metafield out of, and `related` is the
      // endpoint's own default.
      const firstTier = this.tiers.find((tier) => TIER_INTENTS[tier]);
      const rideAlongIntent = firstTier ? TIER_INTENTS[firstTier] : "related";

      const responses = new Map();
      let failed = false;

      const responseFor = async (intent) => {
        if (!responses.has(intent)) {
          responses.set(intent, await this.fetchIntent(productId, intent));
        }

        const response = responses.get(intent);
        failed = failed || response === null;

        return response;
      };

      let cards = null;

      for (const tier of this.tiers) {
        const response = await responseFor(TIER_INTENTS[tier] || rideAlongIntent);

        if (!response) {
          continue;
        }

        const tierCards = response.filter((card) => {
          return tier === "metafield"
            ? card.dataset.tier === "metafield"
            : card.dataset.tier === "recommendations";
        });

        if (tierCards.length) {
          cards = tierCards;
          break;
        }
      }

      if (cards === null && failed) {
        // Nothing found and at least one request broke, so we cannot tell an
        // empty result from a failed one. Deliberately not cached — in memory
        // or in sessionStorage — so a transient blip does not kill this
        // source for the rest of the session. Within this pass it is
        // exhausted anyway, since an empty list yields no pick.
        return [];
      }

      cards = cards || [];
      cache.set(productId, cards);
      persist(this.storageKey);

      return cards;
    }

    // Resolves to every card in the response — both tiers, tagged by
    // `data-tier` — or null when the request itself failed. That distinction
    // matters upstream: "curated nothing" is a real answer worth caching,
    // "the network broke" is not.
    async fetchIntent(productId, intent) {
      const url = `${this.dataset.url}?section_id=header-mini-cart-cross-sells&product_id=${productId}&limit=${this.limit}&intent=${intent}`;

      try {
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Cross-sell recommendations failed (${intent}): ${response.status}`);
        }

        const html = new DOMParser().parseFromString(await response.text(), "text/html");

        return Array.from(html.querySelectorAll(".mini-cart-cross-sell-card"));
      } catch (error) {
        console.error(error);

        return null;
      }
    }
  }

  customElements.define("mini-cart-cross-sells", MiniCartCrossSells);
}
